import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ButtonEl from '../helpers/ctas/Button'
import { getCsrfToken } from '../../helpers/memberSession'
import { pushFormSubmit, useFormStartWhenVisible, attachFormInteractionTracking } from '../../helpers/formAnalytics'

// Issue 1 — endpoint URLs as named constants (no magic strings).
const INVOICE_GET_URL = '/umbraco/surface/invoice/get'
const CAPTURE_CONTEXT_URL = '/umbraco/surface/payment/capture-context'
const PAYMENT_CALLBACK_URL = '/umbraco/surface/payment/callback'

// Issue 2 — single source for the user-facing payment failure message (was duplicated).
const PAYMENT_ERROR_MESSAGE = 'Payment could not be processed. Please try again or contact support.'
const FORM_INIT_ERROR_MESSAGE = 'The secure payment form could not be loaded. Please refresh the page or contact support.'

// Issue 4 — currency formatting literals as named constants.
const CURRENCY_LOCALE = 'en-AU'
const CURRENCY_CODE = 'AUD'

// Styling applied *inside* the CyberSource-hosted iframes. Microform only accepts a limited set
// of CSS properties and values — avoid 'inherit'/keywords it can reject (which leaves the field
// blank). Keep it to concrete values so the input renders consistently with the plain inputs.
const MICROFORM_FIELD_STYLES = {
  input: {
    'font-size': '16px',
    color: '#23282c',
  },
  ':focus': { color: '#23282c' },
  '::placeholder': { color: '#9ca3af' },
}

const INPUT_CLASS =
  'w-full rounded border border-gray-300 px-3 py-2 text-base text-dark! focus:outline-none focus:ring-2 focus:ring-nds-blue'

// Container the Microform iframe is injected into. CyberSource requires the container to have an
// explicit height; the iframe fills it (see .payment-form-page iframe in _iframes.css, which also
// overrides the global opacity:0 that would otherwise hide the field). Height is an inline style
// rather than a Tailwind arbitrary class so it can't be purged. No vertical padding — the iframe
// fills the box; inner text padding comes from the styles object above.
const SECURE_FIELD_HEIGHT = 44
const SECURE_FIELD_CLASS =
  'block w-full rounded border border-gray-300 px-3 bg-white focus-within:ring-2 focus-within:ring-nds-blue'

// Issue 7 — only navigate to same-origin / relative URLs, guarding against open redirects.
function isSafeRedirect(url) {
  try {
    return new URL(url, window.location.origin).origin === window.location.origin
  } catch {
    return false
  }
}

// The capture context is a JWT; its payload carries the Microform client-library URL and the
// Subresource Integrity hash to load it with. Decode the payload (base64url) to read them.
function decodeCaptureContext(jwt) {
  const payload = jwt.split('.')[1]
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
  return JSON.parse(json)
}

// Loads the CyberSource Microform script once, pinned to the integrity hash from the capture
// context. Resolves with the global `Flex` constructor the script registers.
function loadMicroformScript(src, integrity) {
  return new Promise((resolve, reject) => {
    if (window.Flex) {
      resolve(window.Flex)
      return
    }
    const script = document.createElement('script')
    script.src = src
    if (integrity) {
      script.integrity = integrity
      script.crossOrigin = 'anonymous'
    }
    script.onload = () => (window.Flex ? resolve(window.Flex) : reject(new Error('Flex unavailable after load')))
    script.onerror = () => reject(new Error('Failed to load Microform script'))
    document.head.appendChild(script)
  })
}

// Plain field for non-sensitive data (expiry). Card number / CVV use the hosted secure fields.
function PlainField({ id, label, value, onChange, autoComplete, placeholder, maxLength, disabled }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-semibold text-midnight-500">
        {label}
        <span className="text-red-600" aria-hidden="true"> *</span>
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete={autoComplete}
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        aria-required="true"
        disabled={disabled}
        className={INPUT_CLASS}
      />
    </div>
  )
}

PlainField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  autoComplete: PropTypes.string,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  disabled: PropTypes.bool,
}

// Wrapper for a Microform hosted field: renders the label + the container the iframe loads into.
function SecureField({ id, label }) {
  return (
    <div>
      <span id={`${id}-label`} className="mb-1 block text-sm font-semibold text-midnight-500">
        {label}
        <span className="text-red-600" aria-hidden="true"> *</span>
      </span>
      <div id={id} className={SECURE_FIELD_CLASS} style={{ height: `${SECURE_FIELD_HEIGHT}px` }} />
    </div>
  )
}

SecureField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
}

/**
 * Card payment form for CR02 membership renewal/registration payment.
 *
 * Uses CyberSource Flex Microform v2: the card number and security code are entered into
 * CyberSource-hosted iframes (the secure fields), so the PAN/CVV never touch our server. On
 * submit, microform.createToken tokenises the card in the browser and we POST only the resulting
 * transient token to PAYMENT_CALLBACK_URL, which charges via NAB/Cybersource and returns
 * { redirectUrl } on success.
 */
function PaymentFormPage({ title, description, amountDollars, isSectionVisible = true }) {
  const [expirationMonth, setExpirationMonth] = useState('')
  const [expirationYear, setExpirationYear] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formReady, setFormReady] = useState(false)
  const [error, setError] = useState(null)
  const formRef = useRef(null)
  const microformRef = useRef(null)
  const interactionCleanupRef = useRef(null)
  const dlFormType = 'payment'

  // The `amountDollars` prop is a flat configured fallback (NabSettings.RenewalAmountCents).
  // The amount actually charged is the member's real invoice total, which the server caches
  // when /invoice/get runs and reads back via ResolvePaymentAmount. Fetch that same value so
  // the displayed amount matches what will be charged. Fetching also ensures the amount is
  // cached server-side before the member submits.
  const [amount, setAmount] = useState(amountDollars ?? null)

  useEffect(() => {
    let cancelled = false
    fetch(INVOICE_GET_URL, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.amountDollars != null) setAmount(data.amountDollars)
      })
      .catch((err) => {
        // Issue 6 — keep the configured fallback, but surface the failure for diagnosability.
        console.warn('[PaymentFormPage] Could not resolve invoice amount; using fallback.', err)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Initialise the Microform secure fields: fetch the capture context, load the pinned client
  // library, then mount the number + securityCode hosted fields.
  useEffect(() => {
    let cancelled = false

    async function initMicroform() {
      try {
        const res = await fetch(CAPTURE_CONTEXT_URL, { credentials: 'same-origin' })
        if (!res.ok) throw new Error(`capture-context ${res.status}`)
        const { captureContext } = await res.json()
        if (!captureContext) throw new Error('empty capture context')

        const { ctx } = decodeCaptureContext(captureContext)
        const { clientLibrary, clientLibraryIntegrity } = ctx?.[0]?.data ?? {}
        if (!clientLibrary) throw new Error('no client library in capture context')

        const Flex = await loadMicroformScript(clientLibrary, clientLibraryIntegrity)
        if (cancelled) return

        const flex = new Flex(captureContext)
        const microform = flex.microform({ styles: MICROFORM_FIELD_STYLES })
        const number = microform.createField('number', { placeholder: '1234 5678 9012 3456' })
        const securityCode = microform.createField('securityCode', { placeholder: '•••' })

        // Surface field-level failures that don't throw from load() — without this a field that
        // fails to render looks "ready" instead of reporting the problem.
        number.on('error', (e) => console.error('[PaymentFormPage] card number field error', e))
        securityCode.on('error', (e) => console.error('[PaymentFormPage] security code field error', e))

        // Containers are recreated on remount (React StrictMode runs effects twice in dev); clear
        // any previously injected iframe so we don't stack two.
        document.querySelector('#card-number-container')?.replaceChildren()
        document.querySelector('#security-code-container')?.replaceChildren()

        number.load('#card-number-container')
        securityCode.load('#security-code-container')

        microformRef.current = microform
        setFormReady(true)
      } catch (err) {
        console.error('[PaymentFormPage] Microform init failed.', err)
        if (!cancelled) setError(FORM_INIT_ERROR_MESSAGE)
      }
    }

    initMicroform()
    return () => {
      cancelled = true
    }
  }, [])

  useFormStartWhenVisible(dlFormType, { isSectionVisible })

  useEffect(() => {
    if (!formRef.current) return
    interactionCleanupRef.current?.()
    interactionCleanupRef.current = attachFormInteractionTracking(formRef.current)

    return () => {
      interactionCleanupRef.current?.()
      interactionCleanupRef.current = null
    }
  }, [])

  const formattedAmount = amount != null
    ? new Intl.NumberFormat(CURRENCY_LOCALE, { style: 'currency', currency: CURRENCY_CODE }).format(amount)
    : null

  // Tokenise the card in the browser, then POST only the transient token to the server.
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!microformRef.current) {
      setError(FORM_INIT_ERROR_MESSAGE)
      return
    }

    setSubmitting(true)
    setError(null)

    let transientToken
    try {
      // createToken is callback-based — wrap it so we can await the tokenisation result.
      transientToken = await new Promise((resolve, reject) => {
        microformRef.current.createToken({ expirationMonth, expirationYear }, (err, token) => {
          if (err) reject(err)
          else resolve(token)
        })
      })
    } catch (err) {
      // Validation/tokenisation error (e.g. invalid card number or CVV) — keep card fields intact.
      console.warn('[PaymentFormPage] Tokenisation failed.', err?.message ?? err)
      setError('Please check your card details and try again.')
      setSubmitting(false)
      return
    }

    try {
      const res = await fetch(PAYMENT_CALLBACK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'RequestVerificationToken': getCsrfToken(),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ transientToken }),
      })

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        setError(errorData.error ?? PAYMENT_ERROR_MESSAGE)
        return
      }

      const data = await res.json()
      if (data.redirectUrl && isSafeRedirect(data.redirectUrl)) {
        pushFormSubmit(dlFormType)
        window.location.href = data.redirectUrl
        return
      }

      setError(PAYMENT_ERROR_MESSAGE)
    } catch {
      setError(PAYMENT_ERROR_MESSAGE)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="payment-form-page container mx-auto px-5 py-10 lg:px-0 max-w-lg">
      {title && <h2 className="text-midnight-500 mb-4 text-3xl font-semibold">{title}</h2>}
      {description && <p className="text-gray mb-8">{description}</p>}

      {formattedAmount && (
        <div className="mb-6 flex items-baseline justify-between border-b border-t border-gray-200 py-3">
          <span className="font-semibold text-midnight-500">Renewal amount</span>
          <span className="text-2xl font-bold text-midnight-500">{formattedAmount}</span>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded border border-red-300 bg-red-50 p-4" role="alert">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* WCAG 4.1.3 — announce the in-progress payment to assistive tech. */}
      <p className="sr-only" role="status" aria-live="polite">
        {submitting ? 'Processing payment, please wait…' : ''}
      </p>

      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        {/* WCAG 3.3.2 — explain the required-field marker used on each label. */}
        <p className="text-sm text-gray">
          <span className="text-red-600" aria-hidden="true">*</span> indicates a required field
        </p>

        {/* Hosted secure field — card number is captured by CyberSource, never by us. */}
        <SecureField id="card-number-container" label="Card number" />

        <div className="grid grid-cols-2 gap-4">
          <PlainField
            id="expirationMonth"
            label="Expiry month"
            autoComplete="cc-exp-month"
            placeholder="MM"
            maxLength={2}
            value={expirationMonth}
            onChange={setExpirationMonth}
            disabled={submitting}
          />
          <PlainField
            id="expirationYear"
            label="Expiry year"
            autoComplete="cc-exp-year"
            placeholder="YYYY"
            maxLength={4}
            value={expirationYear}
            onChange={setExpirationYear}
            disabled={submitting}
          />
        </div>

        {/* Hosted secure field — security code (CVV) is captured by CyberSource, never by us. */}
        <div className="grid grid-cols-2 gap-4">
          <SecureField id="security-code-container" label="Security code" />
        </div>

        <ButtonEl
          type="submit"
          theme="primary"
          disabled={submitting || !formReady}
          className="w-full justify-center"
        >
          {submitting ? 'Processing…' : !formReady ? 'Loading secure form…' : 'Pay your membership'}
        </ButtonEl>
      </form>
    </div>
  )
}

PaymentFormPage.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  amountDollars: PropTypes.number,
  isSectionVisible: PropTypes.bool,
}

export default PaymentFormPage
